// Shipped default flows (D6-7). Instances start on these; variants are published as
// new versions. Kept in core so the client and the server share one definition.
import { validateFlow } from "./definition.js";
/** Receive a sorted pallet: pallet label/scan, scan every container until the next
 *  pallet or the dock location, sanity-check the tally, aggregate. */
export const RECEIVE_PALLET = validateFlow({
    id: "receive-pallet",
    version: 1,
    name: "Receive pallet",
    process: "receiving",
    start: "pallet",
    note: "Scan the pallet, then every container; scanning the dock location ends the pallet.",
    steps: [
        { id: "pallet", type: "scan_container", title: "Scan pallet label", expect: "logistic-unit", into: "pallet", next: "containers" },
        {
            id: "containers",
            type: "scan_many",
            title: "Scan every container, then the location",
            expect: "trade-item",
            into: "items",
            terminateOn: "location",
            terminatorInto: "dock",
            expectedCount: "expected.count",
            next: "aggregate",
        },
        {
            id: "aggregate",
            type: "emit_event",
            event: "aggregated",
            payload: { parentContainerId: "pallet.containerId", childContainerIds: "items.containerId", completeness: "'verified'" },
            next: null,
        },
    ],
});
/** Put away a container or pallet: scan it, take the proposal, scan the destination. */
export const PUT_AWAY = validateFlow({
    id: "put-away",
    version: 1,
    name: "Put away",
    process: "put_away",
    start: "unit",
    steps: [
        { id: "unit", type: "scan_container", title: "Scan pallet or container", expect: "any", into: "unit", next: "propose" },
        { id: "propose", type: "propose", strategy: "put_away", into: "proposal", next: "destination" },
        { id: "destination", type: "scan_location", title: "Scan destination location", roles: ["storage"], into: "destination", next: "move" },
        {
            id: "move",
            type: "emit_event",
            event: "moved",
            payload: {
                containerIds: "[unit.containerId]",
                fromLocationId: "null",
                toLocationId: "destination",
                proposedLocationId: "proposal.locationId",
                overrideReason: "null",
            },
            next: null,
        },
    ],
});
/** Ad-hoc move (§4.3.1): scan the unit, learn where it stands and whether it sits on a pallet
 *  (the host answers the inquiry), scan the new place; a kolli on a pallet is disaggregated
 *  before it moves. */
export const MOVE = validateFlow({
    id: "move",
    version: 1,
    name: "Move",
    process: "move",
    start: "unit",
    steps: [
        { id: "unit", type: "scan_container", title: "Scan pallet or container", expect: "any", into: "unit", next: "where" },
        { id: "where", type: "inquiry", query: "container", params: { id: "unit.containerId" }, into: "info", next: "destination" },
        { id: "destination", type: "scan_location", title: "Scan the new location", roles: ["storage", "staging", "receiving"], into: "destination", next: "split" },
        { id: "split", type: "branch", next: { branch: [{ when: "info.parentContainerId != null", to: "detach" }], else: "move" } },
        { id: "detach", type: "emit_event", event: "disaggregated", payload: { parentContainerId: "info.parentContainerId", childContainerIds: "[unit.containerId]" }, next: "move" },
        {
            id: "move",
            type: "emit_event",
            event: "moved",
            payload: { containerIds: "[unit.containerId]", fromLocationId: "info.locationId", toLocationId: "destination", proposedLocationId: "null", overrideReason: "null" },
            next: null,
        },
    ],
});
/** Pick a customer order onto outbound pallets (mockup picking-v1, owner's answers 2026-10-28):
 *  the pallet first (scanned or issued), then every carton for the order — any line, wherever
 *  the picker stands — until "Pall klar" ends the pallet. The host decides which line a scan
 *  belongs to, chooses lots by FEFO and records Picked per carton; the flow records that the
 *  cartons sit on the pallet. */
export const PICK = validateFlow({
    id: "pick",
    version: 1,
    name: "Pick",
    process: "picking",
    start: "pallet",
    note: "Scan or issue the outbound pallet, scan every carton for the order, end the pallet.",
    steps: [
        { id: "pallet", type: "scan_container", title: "Scan the outbound pallet", expect: "logistic-unit", into: "pallet", next: "items" },
        { id: "items", type: "scan_many", title: "Scan every carton for the order", expect: "any", into: "items", terminateOn: "done-code", terminatorInto: null, expectedCount: null, next: "aggregate" },
        {
            id: "aggregate",
            type: "emit_event",
            event: "aggregated",
            payload: { parentContainerId: "pallet.containerId", childContainerIds: "items.containerId", completeness: "'verified'" },
            next: null,
        },
    ],
});
/** Count one location (mockup counting-v1 §2): scan the location, then everything standing there;
 *  the host compares with what Trail expects and emits Counted (present, missing, quantities). */
export const COUNT = validateFlow({
    id: "count",
    version: 1,
    name: "Count",
    process: "counting",
    start: "location",
    note: "Scan the location, then every container or pallet standing there; end with the done code.",
    steps: [
        { id: "location", type: "scan_location", title: "Scan the location to count", roles: ["storage", "staging", "receiving"], into: "location", next: "items" },
        { id: "items", type: "scan_many", title: "Scan everything standing here", expect: "any", into: "items", terminateOn: "done-code", terminatorInto: null, expectedCount: null, next: null },
    ],
});
export const DEFAULT_FLOWS = [RECEIVE_PALLET, PUT_AWAY, MOVE, PICK, COUNT];
//# sourceMappingURL=defaults.js.map